import { html, nothing } from "lit";
import type { McpCatalogEntry, McpMessage, McpServerEntry } from "../controllers/mcp";

export type McpViewProps = {
  servers: McpServerEntry[];
  catalog: McpCatalogEntry[];
  loading: boolean;
  error: string | null;
  busyServer: string | null;
  envInputs: Record<string, Record<string, string>>;
  messages: Record<string, McpMessage>;
  onConnect: (name: string) => void;
  onDisconnect: (name: string) => void;
  onEnvInput: (serverName: string, envName: string, value: string) => void;
  onRefresh: () => void;
};

function logoUrl(domain: string): string {
  return `https://logo.clearbit.com/${domain}`;
}

export function renderMcpView(props: McpViewProps) {
  const connectedNames = new Set(props.servers.map((s) => s.name));
  const available = props.catalog.filter((c) => !connectedNames.has(c.name));

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">MCP Servers</div>
          <div class="card-sub">
            Connect external tools to your agent via the Model Context Protocol.
          </div>
        </div>
        <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
          ${props.loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      ${props.error
        ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
        : nothing}

      ${props.servers.length > 0
        ? html`
            <div style="margin-top: 20px;">
              <div style="font-size: 16px; font-weight: 600;">Connected</div>
              <div style="margin-top: 12px; display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px;">
                ${props.servers.map((s) => renderConnectedCard(s, props))}
              </div>
            </div>
          `
        : nothing}

      ${available.length > 0
        ? html`
            <div style="margin-top: 24px;">
              <div style="font-size: 16px; font-weight: 600;">Available</div>
              <div class="muted" style="font-size: 13px; margin-top: 2px;">
                Add your API keys to connect
              </div>
              <div style="margin-top: 12px; display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px;">
                ${available.map((c) => renderCatalogCard(c, props))}
              </div>
            </div>
          `
        : nothing}
    </section>
  `;
}

function renderConnectedCard(server: McpServerEntry, props: McpViewProps) {
  const busy = props.busyServer === server.name;
  const message = props.messages[server.name] ?? null;
  const catalogEntry = props.catalog.find((c) => c.name === server.name);

  return html`
    <div style="border: 1px solid var(--border); border-radius: var(--radius-md, 8px); padding: 16px; display: flex; flex-direction: column; gap: 10px;">
      <div style="display: flex; align-items: center; gap: 10px;">
        ${catalogEntry
          ? html`<img src=${logoUrl(catalogEntry.domain)} alt="" width="24" height="24"
              style="border-radius: 4px; flex-shrink: 0;"
              @error=${(e: Event) => ((e.target as HTMLImageElement).style.display = "none")} />`
          : nothing}
        <div style="flex: 1; min-width: 0;">
          <div style="font-weight: 600; font-size: 14px;">${server.name}</div>
        </div>
        <span class="chip ${server.status === "connected" ? "chip-ok" : "chip-danger"}">
          ${server.status === "connected" ? "Connected" : "Error"}
        </span>
      </div>

      ${server.tools.length > 0
        ? html`<div class="muted" style="font-size: 12px;">
            ${server.toolCount} tool${server.toolCount !== 1 ? "s" : ""}:
            ${server.tools.join(", ")}
          </div>`
        : nothing}

      ${server.error
        ? html`<div style="font-size: 12px; color: var(--danger-color, #d14343);">${server.error}</div>`
        : nothing}

      ${message
        ? html`<div style="font-size: 12px; color: ${message.kind === "error"
            ? "var(--danger-color, #d14343)" : "var(--success-color, #0a7f5a)"};">
            ${message.message}</div>`
        : nothing}

      <button class="btn" style="width: 100%;" ?disabled=${busy}
        @click=${() => props.onDisconnect(server.name)}>
        ${busy ? "Disconnecting..." : "Disconnect"}
      </button>
    </div>
  `;
}

function renderCatalogCard(entry: McpCatalogEntry, props: McpViewProps) {
  const busy = props.busyServer === entry.name;
  const message = props.messages[entry.name] ?? null;
  const envValues = props.envInputs[entry.name] ?? {};
  const allFilled = entry.requiredEnv.every(
    (f) => (envValues[f.name] ?? "").trim().length > 0,
  );

  return html`
    <div style="border: 1px solid var(--border); border-radius: var(--radius-md, 8px); padding: 16px; display: flex; flex-direction: column; gap: 10px;">
      <div style="display: flex; align-items: center; gap: 10px;">
        <img src=${logoUrl(entry.domain)} alt="" width="24" height="24"
          style="border-radius: 4px; flex-shrink: 0;"
          @error=${(e: Event) => ((e.target as HTMLImageElement).style.display = "none")} />
        <div style="flex: 1; min-width: 0;">
          <div style="font-weight: 600; font-size: 14px;">${entry.name}</div>
        </div>
        <span class="chip chip-muted">Available</span>
      </div>

      <div class="muted" style="font-size: 12px; line-height: 1.4;">
        ${entry.description}
      </div>

      ${message
        ? html`<div style="font-size: 12px; color: ${message.kind === "error"
            ? "var(--danger-color, #d14343)" : "var(--success-color, #0a7f5a)"};">
            ${message.message}</div>`
        : nothing}

      ${entry.requiredEnv.map(
        (field) => html`
          <div class="field">
            <label style="font-size: 12px; color: var(--text-muted);">${field.label}</label>
            <input type="password"
              .value=${envValues[field.name] ?? ""}
              @input=${(e: Event) =>
                props.onEnvInput(entry.name, field.name, (e.target as HTMLInputElement).value)}
              placeholder=${field.name}
              style="font-size: 13px;" />
          </div>
        `,
      )}

      <button class="btn primary" style="width: 100%;"
        ?disabled=${busy || !allFilled}
        @click=${() => props.onConnect(entry.name)}>
        ${busy ? "Connecting..." : "Connect"}
      </button>
    </div>
  `;
}
